// Persistent "a new version is available" gate.
//
// WHY THIS IS NOT JUST location.reload():
// a plain reload can still hand the user a stale document. The HTTP cache may
// answer without revalidating, a service worker may serve its own copy, and a
// corporate proxy may do either. So this does three things instead of one:
//
//   1. PURGE  - every Cache Storage entry, every service-worker registration,
//               plus a forced revalidation of the document itself.
//   2. RELOAD - normally location.reload(); if that already failed once, a
//               cache-busted URL the cache has provably never seen.
//   3. VERIFY - on the next boot we check whether the build actually changed.
//               If it did not, we escalate. If we run out of escalations we
//               stop guessing and tell the user how to force it by hand.
//
// The toast is persistent by design: it does not auto-dismiss, and "Later"
// only snoozes it. A newer build cancels any snooze.
//
// Ported from docs/WEB_UPDATE_TOAST.md.

import { useCallback, useEffect, useRef, useState } from 'react';

// ---- the only per-project lines -------------------------------------------
const APP_KEY = 'vtgbackup';               // storage-key prefix; unique per app/origin
const APP_LABEL = 'the backup dashboard';  // shown as "<version> of <APP_LABEL> is ready."
// ---------------------------------------------------------------------------

const VERSION_URL = '/version.json';
const FIRST_CHECK_MS = 5 * 1000;        // let the app paint before the first probe
const POLL_MS = 5 * 60 * 1000;          // background poll
const MIN_GAP_MS = 60 * 1000;           // throttle focus/visibility probes
const SNOOZE_MS = 30 * 60 * 1000;       // how long "Later" hides it
const MAX_ATTEMPTS = 3;                 // reload strategies before we give up
const MIN_ATTEMPT_GAP_MS = 5 * 1000;    // never thrash reloads
const PURGE_BUDGET_MS = 4000;           // a hung cache API must not strand the user
const STALE_REC_MS = 10 * 60 * 1000;    // an attempt older than this is a bug, not a cache

const REC_KEY = APP_KEY + '-update-attempt';   // sessionStorage: survives reloads, dies with the tab
const SNOOZE_KEY = APP_KEY + '-update-snooze'; // localStorage: survives reloads and tabs

export type Remote = { buildId: string; version: string };
type Rec = { target: string; attempt: number; at: number };
type Phase = 'idle' | 'available' | 'applying' | 'stuck' | 'updated';

// ---------- storage (never let a disabled/quota-full store break the app) ----------

function readRec(): Rec | null {
  try {
    const raw = sessionStorage.getItem(REC_KEY);
    if (!raw) return null;
    const r = JSON.parse(raw) as Rec;
    if (!r || typeof r.target !== 'string' || typeof r.attempt !== 'number') return null;
    return r;
  } catch { return null; }
}
function writeRec(r: Rec) { try { sessionStorage.setItem(REC_KEY, JSON.stringify(r)); } catch { /* ignore */ } }
function clearRec() { try { sessionStorage.removeItem(REC_KEY); } catch { /* ignore */ } }

function readSnooze(): { buildId: string; until: number } | null {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as { buildId: string; until: number };
    if (!s || typeof s.buildId !== 'string' || typeof s.until !== 'number') return null;
    return s;
  } catch { return null; }
}
function writeSnooze(buildId: string) {
  try { localStorage.setItem(SNOOZE_KEY, JSON.stringify({ buildId, until: Date.now() + SNOOZE_MS })); } catch { /* ignore */ }
}
function clearSnooze() { try { localStorage.removeItem(SNOOZE_KEY); } catch { /* ignore */ } }

// ---------- the three steps ----------

/** Read the deployed build stamp. Returns null on any doubt - never guess. */
export async function fetchRemote(): Promise<Remote | null> {
  try {
    const res = await fetch(VERSION_URL + '?_=' + Date.now(), {
      cache: 'no-store',
      credentials: 'omit',
      headers: { 'cache-control': 'no-cache', pragma: 'no-cache' },
    });
    if (!res.ok) return null;
    const j = (await res.json()) as Partial<Remote>;
    if (!j || typeof j.buildId !== 'string' || !j.buildId) return null;
    return { buildId: j.buildId, version: String(j.version || '') };
  } catch { return null; }
}

/** Step 1. Best-effort, time-boxed, and individually guarded. */
async function purge(): Promise<void> {
  const jobs: Promise<unknown>[] = [];
  try {
    if (typeof caches !== 'undefined' && caches?.keys) {
      jobs.push(caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))));
    }
  } catch { /* ignore */ }
  try {
    const sw = navigator.serviceWorker;
    if (sw?.getRegistrations) {
      jobs.push(sw.getRegistrations().then((regs) => Promise.all(regs.map((r) => r.unregister()))));
    }
  } catch { /* ignore */ }
  try {
    // Forces the HTTP cache entry for the document to revalidate against the
    // origin, so the reload that follows starts from a known-fresh entry.
    jobs.push(fetch(window.location.href, { cache: 'reload', credentials: 'same-origin' }));
  } catch { /* ignore */ }

  await Promise.race([
    Promise.allSettled(jobs),
    new Promise((resolve) => setTimeout(resolve, PURGE_BUDGET_MS)),
  ]);
}

/**
 * Step 2. Attempt 1 is an ordinary reload; later attempts use a virgin URL.
 * The hash is preserved deliberately - a hash-routed app lands back on the
 * view the user was looking at.
 */
function goFresh(target: string, attempt: number): void {
  if (attempt <= 1) { window.location.reload(); return; }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set('_v', target + '.' + attempt);
    window.location.replace(url.pathname + '?' + url.searchParams.toString() + url.hash);
  } catch {
    window.location.reload();
  }
}

/** Tidy the cache-buster back out of the address bar once it has done its job. */
function stripBustParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_v')) return;
    url.searchParams.delete('_v');
    const q = url.searchParams.toString();
    window.history.replaceState(null, '', url.pathname + (q ? '?' + q : '') + url.hash);
  } catch { /* ignore */ }
}

// ---------- component ----------

export function UpdateGate() {
  const [phase, setPhase] = useState<Phase>('idle');
  const [remote, setRemote] = useState<Remote | null>(null);
  const [snoozedUntil, setSnoozedUntil] = useState(0);
  const lastCheck = useRef(0);
  const applying = useRef(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  const apply = useCallback(async (target: string) => {
    if (applying.current) return;
    applying.current = true;
    setPhase('applying');
    const prev = readRec();
    const attempt = (prev && prev.target === target ? prev.attempt : 0) + 1;
    writeRec({ target, attempt, at: Date.now() });
    await purge();
    goFresh(target, attempt);
  }, []);

  // --- Step 3: did the last attempt actually take? -------------------------
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const rec = readRec();
    if (!rec) return;

    const succeeded = () => {
      clearRec();
      clearSnooze();
      stripBustParam();
      setPhase('updated');
      window.setTimeout(() => setPhase('idle'), 7000);
    };

    // An attempt record that has outlived any reasonable update is evidence of a
    // bug, not a stubborn cache. Never let one strand somebody indefinitely.
    if (Date.now() - rec.at > STALE_REC_MS) { clearRec(); return; }

    if (__BUILD_ID__ === rec.target) { succeeded(); return; }

    // We are not on the build we were chasing — but that target may simply have
    // moved on since. Ask what is deployed NOW before concluding we are stuck,
    // otherwise a deploy during someone's update traps them forever.
    let alive = true;
    void (async () => {
      const current = await fetchRemote();
      if (!alive) return;

      // Matching the current deployment, or being unable to show we are behind,
      // both mean there is nothing left to do.
      if (!current || current.buildId === __BUILD_ID__) { succeeded(); return; }

      if (rec.attempt >= MAX_ATTEMPTS) {
        setRemote(current);
        setPhase('stuck');
        return;
      }
      const wait = Math.max(0, MIN_ATTEMPT_GAP_MS - (Date.now() - rec.at));
      setRemote(current);
      setPhase('applying');
      window.setTimeout(() => { if (alive) void apply(current.buildId); }, wait);
    })();

    return () => { alive = false; };
  }, [apply]);

  // --- Step 0: detection ---------------------------------------------------
  useEffect(() => {
    if (import.meta.env.DEV) return;
    let alive = true;

    const check = async (force: boolean) => {
      if (!alive || applying.current) return;
      const now = Date.now();
      if (!force && now - lastCheck.current < MIN_GAP_MS) return;
      lastCheck.current = now;
      const r = await fetchRemote();
      if (!alive || !r || r.buildId === __BUILD_ID__) return;
      setRemote(r);
      setPhase((p) => (p === 'applying' || p === 'stuck' ? p : 'available'));
    };

    const first = setTimeout(() => void check(true), FIRST_CHECK_MS);
    const iv = setInterval(() => void check(true), POLL_MS);
    const onWake = () => { if (document.visibilityState === 'visible') void check(false); };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      alive = false;
      clearTimeout(first);
      clearInterval(iv);
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, []);

  // --- snooze bookkeeping --------------------------------------------------
  useEffect(() => {
    const s = readSnooze();
    if (!s) return;
    if (s.until <= Date.now()) { clearSnooze(); return; }
    setSnoozedUntil(s.until);
    const t = setTimeout(() => setSnoozedUntil(0), s.until - Date.now());
    return () => clearTimeout(t);
  }, []);

  // Report our height so a transient toast stack can sit above us instead of
  // underneath. Set to 0px whenever we are not on screen.
  // NOTE: declared BEFORE the early return below so hooks stay unconditional.
  useEffect(() => {
    const el = boxRef.current;
    const root = document.documentElement;
    if (!el) { root.style.setProperty('--update-gate-h', '0px'); return; }
    const set = () => root.style.setProperty('--update-gate-h', el.offsetHeight + 8 + 'px');
    set();
    let ro: ResizeObserver | undefined;
    try { ro = new ResizeObserver(set); ro.observe(el); } catch { /* ignore */ }
    return () => { ro?.disconnect(); root.style.setProperty('--update-gate-h', '0px'); };
  }, [phase]);

  const snoozed =
    phase === 'available' &&
    snoozedUntil > Date.now() &&
    readSnooze()?.buildId === remote?.buildId;

  if (phase === 'idle' || snoozed) return null;

  const target = remote?.buildId || '';
  const label = remote?.version ? 'v' + remote.version : 'a new version';

  return (
    <div ref={boxRef} className="update-toast" role="status" aria-live="polite">
      {phase === 'updated' && (
        <>
          <span className="update-dot update-dot-ok" aria-hidden="true" />
          <div className="update-body">
            <div className="update-title">Updated to v{__APP_VERSION__}</div>
            <div className="update-sub">You are on the latest build.</div>
          </div>
        </>
      )}

      {phase === 'applying' && (
        <>
          <span className="update-spin" aria-hidden="true" />
          <div className="update-body">
            <div className="update-title">Updating&hellip;</div>
            <div className="update-sub">Clearing caches and reloading.</div>
          </div>
        </>
      )}

      {phase === 'available' && (
        <>
          <span className="update-dot" aria-hidden="true" />
          <div className="update-body">
            <div className="update-title">Update available</div>
            <div className="update-sub">{label} of {APP_LABEL} is ready.</div>
          </div>
          <div className="update-actions">
            <button
              className="btn btn-sm"
              onClick={() => { if (target) writeSnooze(target); setSnoozedUntil(Date.now() + SNOOZE_MS); }}
            >
              Later
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => void apply(target)}>
              Reload
            </button>
          </div>
        </>
      )}

      {phase === 'stuck' && (
        <>
          <span className="update-dot update-dot-warn" aria-hidden="true" />
          <div className="update-body">
            <div className="update-title">Update needs a hard refresh</div>
            <div className="update-sub">
              Your browser kept serving the old build. Press{' '}
              <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>R</kbd> (<kbd>&#8984;</kbd>+<kbd>Shift</kbd>+<kbd>R</kbd> on a Mac),
              or open the site in a private window. If it keeps saying this,
              &ldquo;Dismiss&rdquo; clears the stuck state.
            </div>
          </div>
          <div className="update-actions">
            {/* An escape hatch that does not depend on the thing that is broken. */}
            <button
              className="btn btn-sm"
              onClick={() => { clearRec(); applying.current = false; setPhase('idle'); }}
            >
              Dismiss
            </button>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => { clearRec(); applying.current = false; void apply(target); }}
            >
              Try again
            </button>
          </div>
        </>
      )}
    </div>
  );
}
